import {
  useContext,
  useEffect,
  useRef,
  useState,
  type TouchEventHandler,
} from 'react'
import { NavigationContext } from './NavigationContext'
import { type Menu, type MenuItem } from './typings'
import { isMenu } from './utils'

export const useNavigationContext = () => useContext(NavigationContext)

export function useMarkActive() {
  const { currentPath } = useNavigationContext()
  const markActive = <M extends Menu | MenuItem>(item: M): M => {
    return isMenu(item)
      ? {
          ...item,
          active:
            item.children.findIndex((child) => child.path === currentPath) >
              -1 ||
            (item.more && item.more.path === currentPath),
          children: item.children.map(markActive),
          more: item.more ? markActive(item.more) : undefined,
        }
      : { ...item, active: item.path === currentPath }
  }
  return markActive
}
export function usePathActive(paths: string | string[]) {
  const { currentPath } = useNavigationContext()

  const pathArray = Array.isArray(paths) ? paths : [paths]

  return pathArray.some((path) => currentPath?.startsWith(path))
}

/**
 * This hook is to amend the \@radix/navigation-menu functionality.
 *
 * There is no way to programmatically access the menu item positioning,
 * so this is what the community came up with:
 *
 * @see https://github.com/radix-ui/primitives/issues/1462#issuecomment-2275683692
 */
function stateChangedOpen(mutation: MutationRecord) {
  return (
    mutation.type === 'attributes' &&
    mutation.attributeName === 'data-state' &&
    (mutation.target as HTMLElement).dataset.state === 'open'
  )
}

export function useMeasurementRef() {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const root = ref.current

    if (!root) {
      return
    }

    const updatePosition = (item: HTMLElement) => {
      const itemRect = item.getBoundingClientRect()
      const rootRect = root.getBoundingClientRect()

      root.style.setProperty(
        '--radix-navigation-menu-item-active-center',
        `${Math.round(itemRect.left - rootRect.left + itemRect.width / 2)}px`,
      )
    }

    const mutationCallback = (mutationsList: MutationRecord[]) => {
      const mutation = mutationsList.find(stateChangedOpen)
      if (mutation) {
        updatePosition(mutation.target as HTMLElement)
      }
    }

    const observer = new MutationObserver(mutationCallback)

    observer.observe(root, {
      attributes: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [ref])

  return ref
}

export function useTouchDistance() {
  const [touchEnded, setTouchEnded] = useState(false)
  const [touchStart, setTouchStart] = useState(0)
  const [touchDistance, setTouchDistance] = useState(0)

  const start: TouchEventHandler<HTMLDivElement> = (event) => {
    setTouchStart(event.changedTouches[0].screenX)
  }

  const move: TouchEventHandler<HTMLDivElement> = (event) => {
    const newDist = Math.max(event.changedTouches[0].screenX - touchStart, 0)
    const diff =
      newDist > touchDistance ? newDist * Math.pow(newDist, -0.3) : newDist
    setTouchDistance(Math.floor(diff * 10) / 10)
  }

  const end: TouchEventHandler<HTMLDivElement> = () => {
    setTouchEnded(true)
    setTimeout(() => {
      setTouchDistance(0)
      setTouchStart(0)
      setTouchEnded(false)
    }, 0)
  }

  return {
    touchDistance,
    touchEnded,
    events: {
      onTouchEnd: end,
      onTouchMove: move,
      onTouchStart: start,
    },
  }
}
